import React, { useState } from 'react';

const ScheduleTab = ({
  weekSchedules,
  shifts,
  loading,
  weekStartDate,
  goToPrevWeek,
  goToNextWeek,
  goToToday,
  formatWeekLabel,
  getWeekDates,
  handleGenerateWeek,
  handlePublishAll,
  lastOptimizationResult
}) => {
  const [showMetrics, setShowMetrics] = useState(false);

  const formatDateForDisplay = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getScheduleForDate = (dateStr) => {
    return weekSchedules[dateStr];
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  // Calculate totals from schedules
  const calculateWeekTotals = () => {
    let totalHours = 0;
    let totalCost = 0;
    let totalAssignments = 0;
    let totalWarnings = 0;

    getWeekDates().forEach(dateStr => {
      const schedules = weekSchedules[dateStr];
      if (schedules && schedules.length > 0) {
        schedules.forEach(schedule => {
          totalHours += schedule.cost_summary?.total_hours || 0;
          totalCost += schedule.cost_summary?.estimated_cost || 0;
          totalAssignments += schedule.shift_assignments?.length || 0;
          totalWarnings += schedule.warnings?.length || 0;
        });
      }
    });

    return { totalHours, totalCost, totalAssignments, totalWarnings };
  };

  const totals = calculateWeekTotals();

  return (
    <div className="weekly-calendar">
      <div className="calendar-header">
        <div className="week-navigation">
          <button className="btn btn-secondary" onClick={goToPrevWeek}>
            &lt;
          </button>
          <span className="week-label">{formatWeekLabel()}</span>
          <button className="btn btn-secondary" onClick={goToNextWeek}>
            &gt;
          </button>
          <button className="btn btn-secondary" onClick={goToToday}>
            Today
          </button>
        </div>
        <div className="week-actions">
          <button
            className="btn btn-primary"
            onClick={() => handleGenerateWeek('ensure_coverage')}
            disabled={loading}
          >
            {loading ? 'Optimizing...' : 'Optimize Week'}
          </button>
          <button
            className="btn btn-success"
            onClick={handlePublishAll}
            disabled={loading}
          >
            Publish All
          </button>
        </div>
      </div>

      {/* Week Summary Stats */}
      <div className="week-summary-bar">
        <div className="summary-stat">
          <span className="stat-label">Total Hours</span>
          <span className="stat-value">{totals.totalHours.toFixed(1)}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Est. Cost</span>
          <span className="stat-value">${totals.totalCost.toFixed(2)}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Assignments</span>
          <span className="stat-value">{totals.totalAssignments}</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Warnings</span>
          <span className={`stat-value ${totals.totalWarnings > 0 ? 'warning' : ''}`}>
            {totals.totalWarnings}
          </span>
        </div>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setShowMetrics(!showMetrics)}
        >
          {showMetrics ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      {/* Optimization Metrics Panel */}
      {showMetrics && lastOptimizationResult && (
        <div className="optimization-metrics-panel">
          <h4>Week Optimization Results</h4>

          {lastOptimizationResult.week_summary && (
            <div className="metrics-section">
              <h5>Hours Breakdown</h5>
              <div className="metrics-grid">
                <div className="metric-item">
                  <span className="metric-label">Regular Hours</span>
                  <span className="metric-value">{lastOptimizationResult.week_summary.regular_hours || 0}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Overtime Hours (&gt;40/wk)</span>
                  <span className="metric-value overtime">{lastOptimizationResult.week_summary.overtime_hours || 0}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Hours</span>
                  <span className="metric-value">{lastOptimizationResult.week_summary.total_hours || 0}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Est. Total Cost</span>
                  <span className="metric-value">${(lastOptimizationResult.week_summary.estimated_cost || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {lastOptimizationResult.optimization_metrics && (
            <>
              {lastOptimizationResult.optimization_metrics.employees_at_soft_limit?.length > 0 && (
                <div className="metrics-section">
                  <h5>Employees at 40+ Hours (Overtime)</h5>
                  <ul className="metrics-list">
                    {lastOptimizationResult.optimization_metrics.employees_at_soft_limit.map((emp, idx) => (
                      <li key={idx} className={emp.wants_overtime ? 'overtime-willing' : 'overtime-unwilling'}>
                        <span className="emp-name">{emp.name}</span>
                        <span className="emp-hours">{emp.hours} hrs</span>
                        <span className="emp-status">
                          {emp.wants_overtime ? '(Wants OT)' : '(No OT preference)'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lastOptimizationResult.optimization_metrics.employees_at_hard_limit?.length > 0 && (
                <div className="metrics-section warning">
                  <h5>Employees at 60 Hour Limit</h5>
                  <ul className="metrics-list">
                    {lastOptimizationResult.optimization_metrics.employees_at_hard_limit.map((emp, idx) => (
                      <li key={idx} className="at-limit">
                        <span className="emp-name">{emp.name}</span>
                        <span className="emp-hours">{emp.hours} hrs</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lastOptimizationResult.optimization_metrics.employees_under_minimum?.length > 0 && (
                <div className="metrics-section info">
                  <h5>Employees Under Minimum Preferred Hours</h5>
                  <ul className="metrics-list">
                    {lastOptimizationResult.optimization_metrics.employees_under_minimum.map((emp, idx) => (
                      <li key={idx}>
                        <span className="emp-name">{emp.name}</span>
                        <span className="emp-hours">{emp.hours} / {emp.minimum} hrs min</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {lastOptimizationResult.optimization_metrics.coverage_gaps?.length > 0 && (
                <div className="metrics-section warning">
                  <h5>Coverage Gaps</h5>
                  <ul className="metrics-list">
                    {lastOptimizationResult.optimization_metrics.coverage_gaps.map((gap, idx) => (
                      <li key={idx}>
                        <span className="gap-date">{gap.date}</span>
                        <span className="gap-shift">{gap.shift}</span>
                        <span className="gap-info">Need {gap.gap} more {gap.role}(s)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {lastOptimizationResult.employee_hours && (
            <div className="metrics-section">
              <h5>Employee Hours Summary</h5>
              <div className="employee-hours-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Days</th>
                      <th>Regular</th>
                      <th>OT</th>
                      <th>Total</th>
                      <th>Preference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(lastOptimizationResult.employee_hours)
                      .filter(emp => emp.hours > 0)
                      .sort((a, b) => b.hours - a.hours)
                      .map((emp, idx) => (
                        <tr key={idx} className={emp.overtime_hours > 0 ? 'has-overtime' : ''}>
                          <td>{emp.name}</td>
                          <td>{emp.days_worked}</td>
                          <td>{emp.regular_hours.toFixed(1)}</td>
                          <td className={emp.overtime_hours > 0 ? 'overtime' : ''}>
                            {emp.overtime_hours.toFixed(1)}
                          </td>
                          <td><strong>{emp.hours.toFixed(1)}</strong></td>
                          <td className="preference">
                            {emp.preferences.min_hours}-{emp.preferences.max_hours} hrs
                            {emp.preferences.wants_overtime && ' (OT OK)'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="calendar-grid">
        {getWeekDates().map(dateStr => {
          const schedules = getScheduleForDate(dateStr);
          const schedule = schedules && schedules.length > 0 ? schedules[0] : null;

          return (
            <div key={dateStr} className={`day-column ${isToday(dateStr) ? 'today' : ''}`}>
              <div className="day-header">
                <span className="day-name">{formatDateForDisplay(dateStr)}</span>
                {schedule && (
                  <span className={`day-status ${schedule.status}`}>
                    {schedule.status}
                  </span>
                )}
              </div>
              <div className="day-shifts">
                {schedule?.shift_assignments?.length > 0 ? (
                  // Group assignments by shift
                  Object.entries(
                    schedule.shift_assignments.reduce((acc, assignment) => {
                      const key = assignment.shift_id;
                      if (!acc[key]) {
                        acc[key] = {
                          shift_id: assignment.shift_id,
                          shift_name: assignment.shift_name,
                          shift_start: assignment.shift_start,
                          shift_end: assignment.shift_end,
                          employees: []
                        };
                      }
                      acc[key].employees.push({
                        name: assignment.employee_name,
                        role: assignment.role_name,
                        is_overtime: assignment.is_overtime
                      });
                      return acc;
                    }, {})
                  ).map(([shiftId, shiftData]) => {
                    const shift = shifts.find(s => s.shift_id === shiftId);
                    const coverage = schedule.coverage_by_shift?.[shiftId];
                    const hasGap = coverage && Object.values(coverage.roles || {}).some(r => r.gap > 0);

                    return (
                      <div
                        key={shiftId}
                        className={`shift-block ${hasGap ? 'understaffed' : ''}`}
                        style={{ borderLeftColor: shift?.color || '#4CAF50' }}
                      >
                        <div className="shift-header">
                          <strong>{shiftData.shift_name}</strong>
                          <span className="shift-time">
                            {shiftData.shift_start} - {shiftData.shift_end}
                          </span>
                        </div>
                        <div className="shift-employees">
                          {shiftData.employees.map((emp, empIdx) => (
                            <div key={empIdx} className={`employee-chip ${emp.is_overtime ? 'overtime' : ''}`}>
                              <span>{emp.name}</span>
                              <span className="employee-role">{emp.role}</span>
                              {emp.is_overtime && <span className="overtime-badge">OT</span>}
                            </div>
                          ))}
                        </div>
                        {hasGap && (
                          <div className="coverage-warning">
                            {Object.entries(coverage.roles || {})
                              .filter(([_, r]) => r.gap > 0)
                              .map(([roleName, r]) => (
                                <span key={roleName} className="gap-info">
                                  Need {r.gap} more {roleName}
                                </span>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="no-shifts">No shifts scheduled</div>
                )}
              </div>
              {schedule && (
                <div className="day-footer">
                  <span className="day-hours">{schedule.cost_summary?.total_hours || 0} hrs</span>
                  <span className="day-cost">${(schedule.cost_summary?.estimated_cost || 0).toFixed(0)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScheduleTab;
